import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { functionAppScope } from "../authConfig";
import { msalInstance } from "../index";
import {
  InteractionRequiredAuthError,
  AccountInfo,
} from "@azure/msal-browser";

// ── Sentinel error for "auth not ready yet" ───────────────────────────────────
// Hooks (e.g. useLoadBonuses, useLoadCommunities) catch this to retry silently
// instead of showing an error snackbar to the user.
export class AuthNotReadyError extends Error {
  constructor() {
    super("Auth not ready — no account or token available");
    this.name = "AuthNotReadyError";
  }
}

// ── Axios instance ────────────────────────────────────────────────────────────

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 60000,
});

// ── Account helper ────────────────────────────────────────────────────────────

function getAccount(): AccountInfo | null {
  return (
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0] ??
    null
  );
}

// ── Token helper ──────────────────────────────────────────────────────────────

async function fetchAccessToken(): Promise<string | null> {
  const account = getAccount();
  if (!account) return null;

  try {
    const resp = await msalInstance.acquireTokenSilent({
      ...functionAppScope,
      account,
    });
    return resp.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Silent acquisition not possible — caller will handle interactive flow
      return null;
    }
    console.error("Token acquisition failed:", e);
    return null;
  }
}

// ── Request interceptor ───────────────────────────────────────────────────────

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const accessToken = await fetchAccessToken();

    if (!accessToken) {
      // Reject with sentinel so hooks can retry silently instead of showing errors
      return Promise.reject(new AuthNotReadyError());
    }

    config.headers["Authorization"] = `Bearer ${accessToken}`;
    config.headers["BonusType"] =
      localStorage.getItem("cba.bonusType") || "ClosingBonus";
    config.headers["RoleType"] = localStorage.getItem("cba.role") || "";

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor ──────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // 401 — token expired mid-session, attempt interactive login
    if (error.response?.status === 401) {
      const account = getAccount();
      if (account) {
        try {
          await msalInstance.acquireTokenRedirect({
            ...functionAppScope,
            account,
          });
        } catch (redirectError) {
          console.error("Token redirect failed:", redirectError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;